import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  deleteAdminLicense,
  getActivationsForLicense,
  listAdminLicenses,
  updateAdminLicenseExpiry,
  updateAdminLicenseStatus,
  type LicenseActivation,
} from '@/services/licenses';
import type { License, LicenseStatus } from '@/domain/types';

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active:   'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  grace:    'bg-amber-50  text-amber-700  ring-1 ring-amber-200',
  inactive: 'bg-surface-100 text-surface-600',
  expired:  'bg-red-50   text-red-600   ring-1 ring-red-200',
  disabled: 'bg-red-50   text-red-700   ring-1 ring-red-200',
  refunded: 'bg-surface-100 text-surface-500',
};

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function addDays(days: number) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function addYears(years: number) {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

// ── Expiry Modal ──────────────────────────────────────────────────────────────

interface ExpiryModalProps {
  license: License;
  onSaved: (updated: License) => void;
  onClose: () => void;
}

function ExpiryModal({ license, onSaved, onClose }: ExpiryModalProps) {
  const [date, setDate]     = useState(license.expiresAt ? license.expiresAt.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const gracePreview = date
    ? new Date(new Date(date).getTime() + 7 * 86_400_000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const save = async (value: string | null) => {
    setSaving(true);
    setError('');
    try {
      const updated = await updateAdminLicenseExpiry(license.id, value);
      onSaved(updated);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
      setSaving(false);
    }
  };

  const quickBtns = [
    { label: '+30 days',  value: addDays(30) },
    { label: '+90 days',  value: addDays(90) },
    { label: '+1 year',   value: addYears(1) },
    { label: '+2 years',  value: addYears(2) },
  ];

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100">
            <div>
              <h3 className="text-sm font-bold text-surface-900">Update Expiry Date</h3>
              <p className="text-xs text-surface-500 mt-0.5">
                {license.productName} · <span className="font-mono">****{license.licenseKeyLast4}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-400 transition-colors"
            >
              <i className="ri-close-line" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">

            {/* Current expiry info */}
            <div className="flex items-center justify-between text-xs bg-surface-50 rounded-lg px-3 py-2.5">
              <span className="text-surface-500">Current expiry</span>
              <span className="font-semibold text-surface-800">
                {license.expiresAt ? fmtDate(license.expiresAt) : 'Lifetime ♾'}
              </span>
            </div>

            {/* Quick presets */}
            <div>
              <p className="text-xs font-semibold text-surface-500 mb-2">Quick extend from today</p>
              <div className="grid grid-cols-2 gap-2">
                {quickBtns.map((q) => (
                  <button
                    key={q.label}
                    type="button"
                    onClick={() => setDate(q.value)}
                    className={`py-2 rounded-lg text-xs font-semibold transition-colors border ${
                      date === q.value
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white text-brand-700 border-brand-200 hover:bg-brand-50'
                    }`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date picker */}
            <div>
              <label className="block text-xs font-semibold text-surface-500 mb-1.5">
                Or pick a specific date
              </label>
              <input
                type="date"
                value={date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
            </div>

            {/* Grace period preview */}
            {gracePreview && (
              <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                <i className="ri-time-line text-amber-500 shrink-0" />
                <span className="text-amber-700">
                  Grace period auto-set to <strong>{gracePreview}</strong>
                </span>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line" /> {error}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => save(date || null)}
              disabled={saving}
              className="w-full py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {saving ? 'Saving…' : date ? `Set expiry to ${fmtDate(date)}` : 'Save as Lifetime'}
            </button>
            <button
              type="button"
              onClick={() => save(null)}
              disabled={saving}
              className="w-full py-2 rounded-xl border border-surface-200 hover:bg-surface-50 text-surface-600 text-xs font-medium transition-colors"
            >
              ♾ Remove expiry — make Lifetime
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 text-surface-400 text-xs hover:text-surface-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ── Sites hover popup ─────────────────────────────────────────────────────────

const POPUP_W = 272;
const POPUP_H_EST = 180;

function SitesCell({ license }: { license: License }) {
  const [open, setOpen]               = useState(false);
  const [activations, setActivations] = useState<LicenseActivation[] | null>(null);
  const [loadingAct, setLoadingAct]   = useState(false);
  const [pos, setPos]                 = useState({ top: 0, left: 0 });
  const cellRef                       = useRef<HTMLDivElement>(null);
  const timerRef                      = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      if (cellRef.current) {
        const rect = cellRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const top = spaceBelow >= POPUP_H_EST + 12
          ? rect.bottom + 6
          : rect.top - POPUP_H_EST - 6;
        const left = Math.min(rect.left, window.innerWidth - POPUP_W - 12);
        setPos({ top, left });
      }
      setOpen(true);
      if (activations === null) {
        setLoadingAct(true);
        getActivationsForLicense(license.id)
          .then((data) => setActivations(data))
          .catch(() => setActivations([]))
          .finally(() => setLoadingAct(false));
      }
    }, 160);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(false);
  };

  const count  = license.activatedSites;
  const limit  = license.siteLimit === 999 ? '∞' : license.siteLimit;
  const hasAny = count > 0;

  return (
    <div
      ref={cellRef}
      className="inline-flex items-center gap-1 cursor-default select-none"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className={`text-xs font-semibold tabular-nums ${hasAny ? 'text-emerald-600' : 'text-surface-400'}`}>
        {count}/{limit}
      </span>
      <i className="ri-information-line text-[11px] text-surface-300" />

      {open && createPortal(
        <div
          className="fixed z-[9999] bg-white border border-surface-200 rounded-2xl shadow-2xl pointer-events-none"
          style={{ top: pos.top, left: pos.left, width: POPUP_W }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-brand-50 flex items-center justify-center">
                <i className="ri-global-line text-brand-600 text-xs" />
              </div>
              <span className="text-xs font-semibold text-surface-800">Activated Sites</span>
            </div>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${hasAny ? 'bg-emerald-50 text-emerald-700' : 'bg-surface-100 text-surface-500'}`}>
              {count}/{limit} used
            </span>
          </div>

          {/* Body */}
          <div className="px-4 py-3">
            {loadingAct ? (
              <div className="py-3 flex items-center justify-center gap-2 text-surface-400 text-xs">
                <i className="ri-loader-4-line animate-spin" /> Loading…
              </div>
            ) : !activations || activations.length === 0 ? (
              <div className="py-3 text-center">
                <i className="ri-global-line text-2xl text-surface-200 block mb-1" />
                <p className="text-xs text-surface-400">No sites activated yet</p>
              </div>
            ) : (
              <ul className="space-y-2 max-h-40 overflow-y-auto">
                {activations.map((a, i) => (
                  <li key={a.id} className="flex items-center gap-2.5 min-w-0">
                    <span className="text-[10px] text-surface-400 w-4 shrink-0 text-right">{i + 1}.</span>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${a.status === 'active' ? 'bg-emerald-400' : 'bg-surface-300'}`} />
                    <span className="text-xs text-surface-700 truncate flex-1 font-mono">{a.siteUrl}</span>
                    {a.status !== 'active' && (
                      <span className="text-[10px] text-surface-400 shrink-0 capitalize bg-surface-100 px-1.5 py-0.5 rounded">{a.status}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminLicenses() {
  const navigate = useNavigate();
  const [licenses, setLicenses]       = useState<License[]>([]);
  const [filter, setFilter]           = useState('all');
  const [search, setSearch]           = useState('');
  const [loading, setLoading]         = useState(true);
  const [busyId, setBusyId]           = useState('');
  const [copiedId, setCopiedId]       = useState('');
  const [error, setError]             = useState('');
  const [expiryLicense, setExpiryLicense] = useState<License | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    listAdminLicenses()
      .then((data) => { if (alive) setLicenses(data); })
      .catch((err) => { if (alive) setError(err?.message ?? 'Unable to load licenses'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = licenses.filter((l) => {
    if (filter !== 'all' && l.status !== filter) return false;
    const q = search.toLowerCase();
    if (q && !l.productName.toLowerCase().includes(q) && !l.userId.toLowerCase().includes(q)) return false;
    return true;
  });

  const handleStatusChange = async (id: string, status: LicenseStatus) => {
    try {
      setBusyId(id);
      setError('');
      const updated = await updateAdminLicenseStatus(id, status);
      setLicenses((prev) => prev.map((l) => (l.id === id ? updated : l)));
    } catch (err) {
      setError((err as Error)?.message ?? 'Unable to update license');
    } finally {
      setBusyId('');
    }
  };

  const handleExpiryUpdate = (updated: License) => {
    setLicenses((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setExpiryLicense(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this license? This cannot be undone.')) return;
    try {
      setBusyId(id);
      setError('');
      await deleteAdminLicense(id);
      setLicenses((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      setError((err as Error)?.message ?? 'Unable to delete license');
    } finally {
      setBusyId('');
    }
  };

  const handleCopy = (license: License) => {
    const key = license.licenseKey ?? `****${license.licenseKeyLast4}`;
    navigator.clipboard.writeText(key).catch(() => {});
    setCopiedId(license.id);
    setTimeout(() => setCopiedId(''), 3000);
  };

  const filterOptions: Array<LicenseStatus | 'all'> = ['all', 'active', 'grace', 'expired', 'disabled', 'inactive'];

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Licenses</h1>
          <p className="text-sm text-surface-500 mt-1">Manage all product licenses and expiry dates.</p>
        </div>
        <button
          onClick={() => navigate('/admin/licenses/generate')}
          className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line mr-1" /> Generate License
        </button>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input
            type="text"
            placeholder="Search by product or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {filterOptions.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                filter === f ? 'bg-brand-600 text-white' : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200 bg-surface-50/60">
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Product</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Customer</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Key</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Sites</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Expires</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 font-semibold text-surface-500 text-xs uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-surface-400 text-sm">
                  <i className="ri-loader-4-line animate-spin text-xl block mb-2" />Loading licenses…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-surface-400 text-sm">No licenses found.</td></tr>
              )}

              {filtered.map((license) => (
                <tr key={license.id} className="border-b border-surface-50 hover:bg-surface-50/50 transition-colors">

                  {/* Product */}
                  <td className="px-5 py-3 font-semibold text-surface-900">{license.productName}</td>

                  {/* Customer */}
                  <td className="px-5 py-3 text-surface-600 text-xs max-w-[160px] truncate">{license.userId}</td>

                  {/* Key + copy */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-surface-500 text-xs">
                        {license.licenseKey ?? `****${license.licenseKeyLast4}`}
                      </span>
                      <div className="relative">
                        <button
                          onClick={() => handleCopy(license)}
                          title="Copy license key"
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-100 text-surface-400 hover:text-surface-600 transition-colors"
                        >
                          {copiedId === license.id
                            ? <i className="ri-check-line text-emerald-600 text-xs" />
                            : <i className="ri-file-copy-line text-xs" />}
                        </button>
                        {copiedId === license.id && (
                          <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-surface-800 px-2 py-0.5 text-xs text-white z-10">
                            Copied!
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Active sites count — hover to see domains */}
                  <td className="px-5 py-3">
                    <SitesCell license={license} />
                  </td>

                  {/* Expiry — click to open modal */}
                  <td className="px-5 py-3">
                    <button
                      onClick={() => setExpiryLicense(license)}
                      title="Edit expiry date"
                      className="group flex flex-col items-start gap-0.5 text-left"
                    >
                      <span className={`text-xs font-medium group-hover:text-brand-600 transition-colors flex items-center gap-1 ${
                        !license.expiresAt ? 'text-brand-600' : 'text-surface-600'
                      }`}>
                        {license.expiresAt ? fmtDate(license.expiresAt) : '♾ Lifetime'}
                        <i className="ri-edit-2-line text-surface-300 group-hover:text-brand-500 transition-colors" />
                      </span>
                      {license.gracePeriodEndsAt && license.expiresAt && (
                        <span className="text-[10px] text-amber-500">
                          Grace: {fmtDate(license.gracePeriodEndsAt)}
                        </span>
                      )}
                    </button>
                  </td>

                  {/* Status badge */}
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[license.status] ?? 'bg-surface-100 text-surface-600'}`}>
                      {license.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <select
                        value={license.status}
                        disabled={busyId === license.id}
                        onChange={(e) => handleStatusChange(license.id, e.target.value as LicenseStatus)}
                        className="px-2 py-1 rounded-lg border border-surface-200 text-xs bg-white disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-brand-400"
                      >
                        {(['active', 'inactive', 'grace', 'disabled', 'expired', 'refunded'] as LicenseStatus[]).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <button
                        disabled={busyId === license.id}
                        onClick={() => handleDelete(license.id)}
                        title="Delete license"
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 disabled:opacity-40 text-red-500 transition-colors"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expiry modal — rendered in document.body via portal */}
      {expiryLicense && (
        <ExpiryModal
          license={expiryLicense}
          onSaved={handleExpiryUpdate}
          onClose={() => setExpiryLicense(null)}
        />
      )}
    </div>
  );
}
